import { ChefHatIcon, CircleDashedIcon, CookingPotIcon, type Icon, LightningIcon } from '@phosphor-icons/react'
import * as React from 'react'

import { cn } from '../lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'

/** The stops, left to right. `Any` is the rest — Parsley decides. */
export enum EffortStop {
  Any = 'any',
  Quick = 'quick',
  Moderate = 'moderate',
  Involved = 'involved'
}

export const EFFORT_STOPS = [EffortStop.Any, EffortStop.Quick, EffortStop.Moderate, EffortStop.Involved] as const
export const EFFORT_LEVELS = [EffortStop.Quick, EffortStop.Moderate, EffortStop.Involved] as const

// The glyph names the level, so the scale reads before any tap. Sizes step up with effort.
const GLYPHS: Record<EffortStop, { icon: Icon; size: string }> = {
  [EffortStop.Any]: { icon: CircleDashedIcon, size: 'size-3.5' },
  [EffortStop.Quick]: { icon: LightningIcon, size: 'size-[0.8125rem]' },
  [EffortStop.Moderate]: { icon: CookingPotIcon, size: 'size-[0.9375rem]' },
  [EffortStop.Involved]: { icon: ChefHatIcon, size: 'size-[1.0625rem]' }
}

const CIRCLE =
  'flex size-[1.875rem] items-center justify-center rounded-full transition-[background-color,color,box-shadow,transform] duration-150 motion-reduce:transition-none'

const HOVER = 'group-hover:ring-1 group-hover:ring-inset group-hover:ring-brand-green-border group-hover:text-brand-green-on-tint'

function circleClass(stop: EffortStop, state: 'disabled' | 'selected' | 'past' | 'rest'): string {
  if (state === 'disabled') return 'bg-gray-100 text-faint dark:bg-gray-800'
  if (state === 'selected') return cn('scale-[1.15] text-white', stop === EffortStop.Any ? 'bg-gray-400' : 'bg-brand-green-fill')
  if (state === 'past')
    return cn(
      'bg-brand-green-tint text-brand-green-on-tint ring-1 ring-inset ring-brand-green-border',
      stop === EffortStop.Any && 'border-[1.5px] border-dashed border-gray-400'
    )
  return cn(
    'bg-card text-faint',
    stop === EffortStop.Any ? 'border-[1.5px] border-dashed border-gray-400' : 'ring-1 ring-inset ring-border',
    HOVER
  )
}

export interface EffortDialProps {
  /** Null shows no pick: every stop at rest and an empty track. */
  value: EffortStop | null
  onChange: (stop: EffortStop) => void
  /** Accessible name for the group, already translated — e.g. "Usually, cooking effort". */
  label: string
  /** Names each stop, already translated — e.g. "Quick — about 30 minutes". */
  stopLabel: (stop: EffortStop) => string
  /** Which stops to show. Defaults to all four; pass `EFFORT_LEVELS` to leave out Any. */
  stops?: readonly EffortStop[]
  /** Shown on hover or focus of each stop. */
  stopTooltip?: (stop: EffortStop) => React.ReactNode
  disabled?: boolean
  className?: string
}

/**
 * A single-select dial for cooking effort — Any, Quick, Moderate, Involved — with each stop drawn
 * as its level's glyph. A track fills to the pick, and the stops it passes tint.
 *
 * Built on native radios: arrow keys and the single tab stop come from the browser.
 */
export function EffortDial({
  value,
  onChange,
  label,
  stopLabel,
  stops = EFFORT_STOPS,
  stopTooltip,
  disabled = false,
  className
}: EffortDialProps) {
  // Radios group by `name`, so each dial on a page needs its own.
  const groupName = React.useId()
  const index = value === null ? -1 : Math.max(0, stops.indexOf(value))
  const filled = !disabled && value !== null && value !== EffortStop.Any
  const fill = filled && stops.length > 1 ? (index / (stops.length - 1)) * 100 : 0

  return (
    <span
      role="radiogroup"
      aria-label={label}
      aria-disabled={disabled || undefined}
      className={cn('relative inline-flex gap-0.5', className)}>
      {/* The track runs centre to centre; the circles sit over it. */}
      <span aria-hidden="true" className="absolute inset-x-5 top-[1.3125rem] h-0.5 bg-track">
        <span
          className="absolute inset-y-0 left-0 bg-brand-green-600 transition-[width] duration-[180ms] ease-[cubic-bezier(.2,.7,.2,1)] motion-reduce:transition-none dark:bg-brand-green-400"
          style={{ width: `${fill}%` }}
        />
      </span>
      {stops.map((stop, i) => {
        const { icon: Glyph, size } = GLYPHS[stop]
        const state = disabled ? 'disabled' : stop === value ? 'selected' : filled && i < index ? 'past' : 'rest'
        const target = (
          <label
            key={stop}
            // Not through `cn`: tailwind-merge reads `outline-[3px]` as clashing with `outline` and drops the style.
            className={`group relative flex h-11 w-10 items-center justify-center rounded-xl has-[:focus-visible]:outline has-[:focus-visible]:outline-[3px] has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-pelorous-500 dark:has-[:focus-visible]:outline-pelorous-300 ${disabled ? 'cursor-default' : 'cursor-pointer'}`}>
            <input
              type="radio"
              name={groupName}
              value={stop}
              checked={stop === value}
              disabled={disabled}
              onChange={() => onChange(stop)}
              className="sr-only"
            />
            <span className="sr-only">{stopLabel(stop)}</span>
            <span className={cn(CIRCLE, circleClass(stop, state))}>
              <Glyph weight="bold" aria-hidden="true" className={size} />
            </span>
          </label>
        )
        if (!stopTooltip || disabled) return target
        return (
          <Tooltip key={stop}>
            <TooltipTrigger asChild>{target}</TooltipTrigger>
            <TooltipContent>{stopTooltip(stop)}</TooltipContent>
          </Tooltip>
        )
      })}
    </span>
  )
}
